import { HTMLAttributes, useState, forwardRef, ReactNode, InputHTMLAttributes } from 'react';
import cn from 'classnames';
import { ReactComponent as EyeOpen } from '@packages/shared/assets/eye_open.svg';
import { ReactComponent as EyeClosed } from '@packages/shared/assets/eye_closed.svg';
import { DataTestId } from '@packages/shared/core/common/constants/dataTestId';
import IconButton from '../IconButton';
import styles from './TextField.module.scss';

export type TextFieldProps = {
  wrapperClass?: string;
  isValid?: boolean;
  error?: string;
  label?: string;
  labelProps?: HTMLAttributes<HTMLLabelElement>;
  renderUnderlay?: () => ReactNode;
  errorClass?: string;
} & InputHTMLAttributes<HTMLInputElement>;

const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const {
    type = 'text',
    isValid = false,
    error = '',
    wrapperClass = '',
    errorClass = '',
    className = '',
    label,
    id,
    labelProps = {},
    renderUnderlay,
    ...otherProps
  } = props;

  const isError = !!error;

  const containerClasses = cn({
    [styles.container]: true,
    [styles.containerError]: isError,
    [styles.containerSuccess]: isValid,
    [wrapperClass]: !!wrapperClass,
  });

  const isPasswordField = type === 'password';

  const inputClasses = cn({
    [styles.textInput]: true,
    [styles.inputError]: isError,
    [styles.textInputPassword]: isPasswordField,
    [className]: !!className,
  });

  const errorClasses = cn({
    [styles.errorText]: true,
    [errorClass]: !!errorClass,
  });

  const renderPasswordIcon = () => (
    <IconButton className={styles.eyeButton} onClick={() => setIsPasswordVisible((val) => !val)}>
      {isPasswordVisible ? <EyeOpen className={styles.eyeIcon} /> : <EyeClosed className={styles.eyeIcon} />}
    </IconButton>
  );

  return (
    <div className={containerClasses}>
      {label && (
        <label {...labelProps} htmlFor={id} className={cn(styles.label, labelProps.className)}>
          {label}
        </label>
      )}

      <div className={styles.textInputWrapper}>
        <input
          {...otherProps}
          id={id}
          className={inputClasses}
          type={isPasswordVisible && isPasswordField ? 'text' : type}
          ref={ref}
        />

        {isPasswordField && renderPasswordIcon()}
      </div>

      <span className={errorClasses} role="alert" data-testid={DataTestId.ERROR_MESSAGE}>
        {error}
      </span>

      {!!renderUnderlay && renderUnderlay()}
    </div>
  );
});

export default TextField;
